import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import "./BookDemo.css";

export function BookDemo() {
  const navigate = useNavigate();
  
  // Form state
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    date: "",
    timeSlot: "",
    message: "",
  });

  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);

  // Time slots (30-minute intervals)
  const allTimeSlots = [
    "09:00 AM - 09:30 AM",
    "09:30 AM - 10:00 AM",
    "10:00 AM - 10:30 AM",
    "10:30 AM - 11:00 AM",
    "11:00 AM - 11:30 AM",
    "11:30 AM - 12:00 PM",
    "12:00 PM - 12:30 PM",
    "12:30 PM - 01:00 PM",
    "01:00 PM - 01:30 PM",
    "01:30 PM - 02:00 PM",
    "02:00 PM - 02:30 PM",
    "02:30 PM - 03:00 PM",
    "03:00 PM - 03:30 PM",
    "03:30 PM - 04:00 PM",
    "04:00 PM - 04:30 PM",
    "04:30 PM - 05:00 PM",
    "05:00 PM - 05:30 PM",
    "05:30 PM - 06:00 PM",
  ];

  // Get minimum date (today)
  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  // Fetch available slots when date changes
  useEffect(() => {
    if (formData.date) {
      fetchAvailableSlots(formData.date);
    } else {
      setAvailableSlots(allTimeSlots);
    }
  }, [formData.date]);

  const fetchAvailableSlots = async (date) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/demo/available-slots`,
        {
          params: { date },
        }
      );

      if (response.data.success) {
        setAvailableSlots(response.data.availableSlots);
      }
    } catch (error) {
      console.error("Error fetching available slots:", error);
      setAvailableSlots(allTimeSlots);
    }
  };

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  // Handle time slot selection
  const handleTimeSlotSelect = (slot) => {
    if (!availableSlots.includes(slot)) return;

    setFormData((prev) => ({
      ...prev,
      timeSlot: slot,
    }));

    if (errors.timeSlot) {
      setErrors((prev) => ({
        ...prev,
        timeSlot: "",
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!/^[\d\s\-\+\(\)]{10,}$/.test(formData.phone)) {
      newErrors.phone = "Invalid phone number";
    }

    if (!formData.company.trim()) {
      newErrors.company = "Company name is required";
    }

    if (!formData.date) {
      newErrors.date = "Please select a date";
    }

    if (!formData.timeSlot) {
      newErrors.timeSlot = "Please select a time slot";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fill in all required fields correctly");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/demo/book-demo`,
        formData
      );

      if (response.data.success) {
        setSuccess(true);
        toast.success("Demo booked successfully! Check your email for confirmation.");
        
        // Reset form
        setFormData({
          name: "",
          email: "",
          phone: "",
          company: "",
          date: "",
          timeSlot: "",
          message: "",
        });

        // Scroll to top
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } catch (error) {
      console.error("Error booking demo:", error);
      
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to book demo. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bd-page">
      <div className="bd-hero">
        <div className="bd-container">
          <div className="bd-badge">
            <div className="bd-badge-icon">📅</div>
            <span>Schedule Your Free Demo</span>
          </div>

          <h1 className="bd-title">
            Book a <span className="bd-gradient-text">Demo</span>
          </h1>

          <p className="bd-subtitle">
            See how Hussme can transform your business with our smart digital
            marketing tools. Choose a convenient time and we'll walk you through
            everything.
          </p>
        </div>
      </div>

      <div className="bd-container">
        {success && (
          <div className="bd-success-banner">
            <h3>🎉 Demo Booked Successfully!</h3>
            <p>
              We've sent a confirmation email with all the details. Our team will
              contact you shortly.
            </p>
          </div>
        )}

        <div className="bd-form-container">
          <div className="bd-info-box">
            <p>
              <strong>What to expect:</strong> A 30-minute personalized walkthrough
              of Hussme's features, tailored to your business needs. No pressure,
              just insights.
            </p>
          </div>

          <form className="bd-form" onSubmit={handleSubmit}>
            <div className="bd-form-row">
              <div className="bd-form-group">
                <label className="bd-label">
                  Full Name <span className="bd-required">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  className="bd-input"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={handleChange}
                />
                {errors.name && (
                  <div className="bd-error-message">
                    <svg
                      className="bd-error-icon"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {errors.name}
                  </div>
                )}
              </div>

              <div className="bd-form-group">
                <label className="bd-label">
                  Email Address <span className="bd-required">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  className="bd-input"
                  placeholder="john@company.com"
                  value={formData.email}
                  onChange={handleChange}
                />
                {errors.email && (
                  <div className="bd-error-message">
                    <svg
                      className="bd-error-icon"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {errors.email}
                  </div>
                )}
              </div>
            </div>

            <div className="bd-form-row">
              <div className="bd-form-group">
                <label className="bd-label">
                  Phone Number <span className="bd-required">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  className="bd-input"
                  placeholder="+1 (555) 123-4567"
                  value={formData.phone}
                  onChange={handleChange}
                />
                {errors.phone && (
                  <div className="bd-error-message">
                    <svg
                      className="bd-error-icon"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {errors.phone}
                  </div>
                )}
              </div>

              <div className="bd-form-group">
                <label className="bd-label">
                  Company Name <span className="bd-required">*</span>
                </label>
                <input
                  type="text"
                  name="company"
                  className="bd-input"
                  placeholder="Your Company Inc."
                  value={formData.company}
                  onChange={handleChange}
                />
                {errors.company && (
                  <div className="bd-error-message">
                    <svg
                      className="bd-error-icon"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {errors.company}
                  </div>
                )}
              </div>
            </div>

            <div className="bd-form-group">
              <label className="bd-label">
                Preferred Date <span className="bd-required">*</span>
              </label>
              <input
                type="date"
                name="date"
                className="bd-input"
                min={getMinDate()}
                value={formData.date}
                onChange={handleChange}
              />
              {errors.date && (
                <div className="bd-error-message">
                  <svg
                    className="bd-error-icon"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {errors.date}
                </div>
              )}
            </div>

            <div className="bd-form-group">
              <label className="bd-label">
                Select Time Slot <span className="bd-required">*</span>
              </label>
              {formData.date ? (
                <div className="bd-time-slots">
                  {allTimeSlots.map((slot) => {
                    const isAvailable = availableSlots.includes(slot);
                    const isSelected = formData.timeSlot === slot;

                    return (
                      <div
                        key={slot}
                        className={`bd-time-slot ${
                          isSelected ? "bd-time-slot-selected" : ""
                        } ${!isAvailable ? "bd-time-slot-disabled" : ""}`}
                        onClick={() => handleTimeSlotSelect(slot)}
                      >
                        {slot}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p style={{ fontSize: "14px", color: "var(--bd-text-muted)" }}>
                  Please select a date first to see available time slots
                </p>
              )}
              {errors.timeSlot && (
                <div className="bd-error-message">
                  <svg
                    className="bd-error-icon"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {errors.timeSlot}
                </div>
              )}
            </div>

            <div className="bd-form-group">
              <label className="bd-label">
                Additional Message <span style={{ color: "var(--bd-text-muted)", fontWeight: 400 }}>(Optional)</span>
              </label>
              <textarea
                name="message"
                className="bd-textarea"
                placeholder="Tell us about your business and what you'd like to learn..."
                value={formData.message}
                onChange={handleChange}
              ></textarea>
            </div>

            <button
              type="submit"
              className="bd-submit-btn"
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="bd-spinner"></div>
                  Booking...
                </>
              ) : (
                <>
                  Book My Demo
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </>
              )}
            </button>
          </form>

          <ul className="bd-features">
            <li className="bd-feature">
              <div className="bd-feature-icon">✓</div>
              <span>30-minute personalized demo</span>
            </li>
            <li className="bd-feature">
              <div className="bd-feature-icon">✓</div>
              <span>No credit card required</span>
            </li>
            <li className="bd-feature">
              <div className="bd-feature-icon">✓</div>
              <span>Learn about pricing options</span>
            </li>
            <li className="bd-feature">
              <div className="bd-feature-icon">✓</div>
              <span>Get your questions answered</span>
            </li>
          </ul>
        </div>

        <div className="bd-contact-info">
          <h3>Need to talk to someone now?</h3>
          <p>
            Email us at{" "}
            <a href="mailto:support@hussme.com" className="bd-contact-link">
              support@hussme.com
            </a>{" "}
            or call us during business hours.
          </p>
        </div>
      </div>
    </div>
  );
}

export default BookDemo;
